import type {
  AssetsAssetSchemaTypedWithId,
  AssetsPlaylistSchemaWithId,
} from '@iconik-io/iconik-api/src/services/assets';
import type { FilesFormatSchemaWithId } from '@iconik-io/iconik-api/src/services/files';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';

import {
  isSearchItemEntity,
  type SearchItemEntity,
} from '../redux/entities/normalized/search_item';
import { groupByObjectType } from '../utils/group_by_object_type';
import type { EntityWithObjectType } from '../utils/object_type';
import type {
  DownloadAsset,
  DownloadAssetVersion,
  DownloadItemFormat,
  DownloadRequestBody,
} from './agent_api_client';

export type ItemIdVersionsMap = { [itemId: string]: string | undefined };

export type DownloadType = 'proxy' | 'original' | 'subtitles' | 'all';

export const typeFormatMap: { [type in DownloadType]: DownloadItemFormat } = {
  proxy: 'ICONIK_PROXY',
  original: 'ORIGINAL',
  subtitles: 'SUBTITLES',
  all: 'DOWNLOAD_ALL_FORMATS',
};

export function isArrayOfAssetsOrSearchItems(
  items: (
    | SearchItemEntity
    | AssetsPlaylistSchemaWithId
    | AssetsAssetSchemaTypedWithId
    | FilesFormatSchemaWithId
  )[],
): items is SearchItemEntity[] {
  return items.every(
    (item): item is SearchItemEntity => !('playlist_items' in item),
  );
}

export function isArrayOfSearchItems(
  items: (
    | SearchItemEntity
    | AssetsAssetSchemaTypedWithId
    | AssetsPlaylistSchemaWithId
    | FilesFormatSchemaWithId
  )[],
): items is SearchItemEntity[] {
  return items.every(isSearchItemEntity);
}

export function isArrayOfFormats(
  items: (
    | SearchItemEntity
    | AssetsPlaylistSchemaWithId
    | AssetsAssetSchemaTypedWithId
    | FilesFormatSchemaWithId
  )[],
): items is FilesFormatSchemaWithId[] {
  return items.every((item) => 'storage_methods' in item);
}

export function isArrayOfPlaylists(
  items: (
    | SearchItemEntity
    | AssetsPlaylistSchemaWithId
    | AssetsAssetSchemaTypedWithId
    | FilesFormatSchemaWithId
  )[],
): items is AssetsPlaylistSchemaWithId[] {
  return items.every((item) => 'playlist_items' in item);
}

export function isArrayOfAssets(
  items: (
    | SearchItemEntity
    | AssetsPlaylistSchemaWithId
    | AssetsAssetSchemaTypedWithId
    | FilesFormatSchemaWithId
  )[],
): items is AssetsAssetSchemaTypedWithId[] {
  return items.every(
    (item) =>
      !('object_type' in item) &&
      !('playlist_items' in item) &&
      !('storage_methods' in item),
  );
}

export function itemsToDownloadRequest(
  items: (
    | SearchItemEntity
    | AssetsPlaylistSchemaWithId
    | AssetsAssetSchemaTypedWithId
    | FilesFormatSchemaWithId
  )[],
  itemIdVersionsMap: ItemIdVersionsMap,
  type: DownloadType,
  subtitleFormats?: FilesFormatSchemaWithId[],
): DownloadRequestBody {
  let assets:
    | EntityWithObjectType[]
    | AssetsAssetSchemaTypedWithId[]
    | undefined;
  let collections: EntityWithObjectType[] | undefined;
  let playlists: AssetsPlaylistSchemaWithId[] | undefined;

  if (isArrayOfSearchItems(items)) {
    const groupedItems = groupByObjectType(
      [OBJECT_TYPE_ASSETS, OBJECT_TYPE_COLLECTIONS],
      items,
    );
    assets = groupedItems.get(OBJECT_TYPE_ASSETS);
    collections = groupedItems.get(OBJECT_TYPE_COLLECTIONS);
  }

  if (isArrayOfAssets(items)) {
    assets = items;
  }

  if (isArrayOfPlaylists(items)) {
    playlists = items;
  }

  let downloadAssets: DownloadAsset[] | undefined;
  let downloadAssetVersions: DownloadAssetVersion[] | undefined;

  if (isArrayOfFormats(items)) {
    for (const formatValue of items) {
      if (formatValue.asset_id) {
        if (formatValue.version_id) {
          if (!downloadAssetVersions) {
            downloadAssetVersions = [];
          }
          downloadAssetVersions.push({
            assetId: formatValue.asset_id,
            versionId: formatValue.version_id,
            format: formatValue.name,
            formatId: formatValue.id,
          });
        } else {
          if (!downloadAssets) {
            downloadAssets = [];
          }
          downloadAssets.push({
            assetId: formatValue.asset_id,
            format: formatValue.name,
            formatId: formatValue.id,
          });
        }
      }
    }
  }

  for (const asset of assets ?? []) {
    const versionId = itemIdVersionsMap[asset.id];

    if (type === 'subtitles' && subtitleFormats && subtitleFormats.length > 0) {
      // Filter subtitles for this asset and create a download entry for each
      const assetSubtitles = subtitleFormats.filter(
        (format) => format.asset_id === asset.id,
      );

      for (const subtitle of assetSubtitles) {
        if (versionId) {
          if (!downloadAssetVersions) {
            downloadAssetVersions = [];
          }
          downloadAssetVersions.push({
            assetId: asset.id,
            versionId,
            format: 'SUBTITLES',
            formatId: subtitle.id,
          });
        } else {
          if (!downloadAssets) {
            downloadAssets = [];
          }
          downloadAssets.push({
            assetId: asset.id,
            format: 'SUBTITLES',
            formatId: subtitle.id,
          });
        }
      }
    } else {
      // Standard download (non-subtitle or no formats provided)
      if (versionId) {
        if (!downloadAssetVersions) {
          downloadAssetVersions = [];
        }
        downloadAssetVersions.push({
          assetId: asset.id,
          versionId,
          format: typeFormatMap[type],
        });
      } else {
        if (!downloadAssets) {
          downloadAssets = [];
        }

        downloadAssets.push({
          assetId: asset.id,
          format: typeFormatMap[type],
        });
      }
    }
  }

  const downloadCollections = collections?.map(({ id }) => ({
    collectionId: id,
    format: typeFormatMap[type],
  }));

  const downloadPlaylists = playlists?.map(({ id }) => ({
    playlistId: id,
    format: typeFormatMap[type],
  }));

  return {
    assets: downloadAssets,
    assetVersions: downloadAssetVersions,
    collections: downloadCollections,
    playlists: downloadPlaylists,
  };
}
